<script setup lang="ts">
import { ref, type PropType } from 'vue'
import { i18n } from '@/i18n'
import type { Category, LikeableResourceGroup } from '@/models'
import { Category_List } from '@/models'
import OpenClose from '@/components/OpenClose.vue'
import ResourceCard from '@/components/resources/ResourceCard.vue'
import { categoryColour } from '@/constants'
import RightArrow from '@/assets/rightArrow.svg'
const t = i18n.global.t

defineProps({
  likedResourceList: {
    type: Array as PropType<LikeableResourceGroup[]>,
    required: true,
  },
  likeMode: { type: String as PropType<'build' | 'like' | 'none'>, default: 'build' },
  mode: { type: String as PropType<'build' | 'regular'>, default: 'regular' },
})

const openSelectedItem = ref<number>(-1)
const menuSelectedItem = ref<number>(-1)
const selectedCategory = ref<Category | undefined>()
</script>

<template>
  <!-- Category collapsible sections: each contains filtered resource groups -->
  <div
    class="flex flex-col items-center text-center w-full rounded-t-2xl border border-white shadow-md mt-4"
    v-for="(c, i) of Category_List"
    :key="c"
    :class="categoryColour(c).background + ' ' + categoryColour(c).text"
  >
    <!-- Category header: expand toggle and options menu -->
    <div class="w-full flex flex-row items-center justify-between p-4 px-8">
      <p class="text-3xl font-bold truncate">{{ t(c) }}</p>
      <div class="flex flex-row items-center">
        <div
          @click="openSelectedItem === i ? (openSelectedItem = -1) : (openSelectedItem = i)"
          class="w-6 h-6 rounded-full flex items-center justify-center cursor-pointer bg-[#042E2C]"
        >
          <img
            :src="RightArrow"
            class="transition-all duration-500 ease-out w-3 h-3 invert"
            :class="[openSelectedItem === i ? 'rotate-270' : 'rotate-90']"
          />
        </div>
        <div
          @pointerdown="menuSelectedItem = i"
          @pointerup="menuSelectedItem = -1"
          class="ml-4 h-12 rounded-full flex items-center justify-center cursor-pointer"
        >
          <svg class="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="5" cy="12" r="2" />
            <circle cx="12" cy="12" r="2" />
            <circle cx="19" cy="12" r="2" />
          </svg>
        </div>
      </div>
    </div>

    <!-- Resource groups filtered by current category -->
    <OpenClose
      :open="openSelectedItem === i"
      class="w-full bg-white text-black"
      :class="[openSelectedItem === i ? 'border-t border-white' : '']"
    >
      <template
        v-for="rg of likedResourceList.filter((rg) => rg.category === c)"
        :key="rg.documentId"
      >
        <div v-if="rg.resources.length" class="flex flex-row items-center pl-10">
          <img
            :src="RightArrow"
            class="w-4"
            :class="{
              invert: selectedCategory == c,
            }"
          />
          <p class="w-full m-4 font-normal text-2xl text-left">{{ rg.description }}</p>
        </div>
        <div
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 max-w-max max-sm:mx-auto gap-3 mb-4 px-4"
        >
          <ResourceCard
            v-for="r of rg.resources"
            :key="r.documentId"
            :resource="r"
            :liked="r.liked"
            :category="c"
            :like-mode="likeMode"
            :mode="mode"
          />
        </div>
      </template>
    </OpenClose>
  </div>
</template>

<style scoped></style>
